import type { ReactNode } from "react";
import type { Block } from "./types";
import { Link } from "../router/Router";
import { CodeBlock } from "../components/CodeBlock";

/** Render text with `backtick` spans as inline code. Text without backticks is returned as-is. */
export function inline(text: string): ReactNode {
  if (!text.includes("`")) return text;
  return text.split(/(`[^`]+`)/g).map((part, i) =>
    part.length > 2 && part.startsWith("`") && part.endsWith("`") ? (
      <code key={i} className="hc-inline-code">
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    ),
  );
}

// Renders a single content block. Kept intentionally small — the guide model
// only needs a handful of block types.
export function renderBlock(block: Block, key: number): ReactNode {
  switch (block.type) {
    case "p":
      return (
        <p key={key} className="hc-p">
          {inline(block.text)}
        </p>
      );
    case "subheading":
      return (
        <h4 key={key} className="hc-subheading">
          {block.text}
        </h4>
      );
    case "ul":
      return (
        <ul key={key} className="hc-ul">
          {block.items.map((it, i) => (
            <li key={i}>{inline(it)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="hc-ol">
          {block.items.map((it, i) => (
            <li key={i}>{inline(it)}</li>
          ))}
        </ol>
      );
    case "note":
      return (
        <div key={key} className={`hc-note hc-note--${block.tone ?? "info"}`}>
          <span className="hc-note__label">
            {block.tone === "warning"
              ? "Important"
              : block.tone === "tip"
                ? "Tip"
                : "Note"}
          </span>
          <span>{inline(block.text)}</span>
        </div>
      );
    case "steps-ref":
      return (
        <p key={key} className="hc-p">
          <Link to={block.to} className="hc-inline-link">
            {block.label} →
          </Link>
        </p>
      );
    case "video":
      return (
        <figure key={key} className="hc-video">
          <video controls preload="metadata" playsInline poster={block.poster} src={block.src}>
            Your browser can’t play this video.
          </video>
          {block.caption && <figcaption className="hc-video__caption">{block.caption}</figcaption>}
        </figure>
      );
    case "table":
      return (
        <div key={key} className="hc-table-wrap">
          <table className="hc-table">
            <thead>
              <tr>
                {block.head.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "code":
      return <CodeBlock key={key} code={block.code} lang={block.lang} title={block.title} />;
    default:
      return null;
  }
}
